<template>
  <div class="page-about">
  <main>
      <section class="relative overflow-hidden bg-gray-50">
        <div class="absolute inset-0 bg-gradient-to-r from-brand/5 via-transparent to-brand/5"></div>
        <div class="mx-auto max-w-[1400px] px-4 py-16 lg:flex lg:items-center lg:gap-12 lg:px-8 lg:py-20">
          <div class="relative z-10 space-y-4 lg:w-7/12">
            <p class="text-xs font-semibold uppercase text-muted">Our story</p>
            <h1 class="text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">We are the event and invite marketplace built for clarity, trust, and joy.</h1>
            <p class="text-lg text-muted">Invi8 brings invitations, guest communication, venues and event services into one connected experience—so hosts can spend less time switching tools and more time bringing people together.</p>
            <div class="flex flex-wrap gap-3">
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand shadow-card ring-1 ring-gray-100">Invitations</span>
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-100">Guest management</span>
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-100">Event discovery</span>
            </div>
          </div>
          <div class="relative z-10 mt-10 flex-1 lg:mt-0">
            <div class="rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
              <img class="h-64 w-full rounded-2xl bg-orange-50/60 object-contain p-5" src="/assets/illustrations/team-collaboration.svg" alt="A team collaborating on an event">
              <div class="mt-4 grid gap-3 sm:grid-cols-2">
                <div class="rounded-2xl bg-gray-50 p-4">
                  <p class="text-sm font-semibold text-ink">Built with hosts</p>
                  <p class="text-sm text-muted">Clear tools help hosts coordinate the details without losing sight of the experience.</p>
                </div>
                <div class="rounded-2xl bg-gray-50 p-4">
                  <p class="text-sm font-semibold text-ink">Vendors first</p>
                  <p class="text-sm text-muted">Relevant profiles and enquiries help event professionals connect with the right hosts.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div class="grid gap-6 md:grid-cols-3">
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Transparency</p>
            <p class="mt-2 text-sm text-muted">Event details, guest responses, and updates stay visible in one organized experience.</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Trust</p>
            <p class="mt-2 text-sm text-muted">Event access and guest-facing actions follow the privacy controls chosen by the host.</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Delight</p>
            <p class="mt-2 text-sm text-muted">Invitations can combine useful event information with photos, videos, and shared memories.</p>
          </div>
        </div>
      </section>

      <section class="bg-gray-900">
        <div class="mx-auto max-w-[1400px] px-4 py-12 text-white lg:px-8">
          <div class="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div class="space-y-4">
              <p class="text-xs font-semibold uppercase text-gray-400">Milestones</p>
              <h2 class="text-3xl font-semibold leading-tight">We’re just getting started.</h2>
              <p class="text-lg text-gray-300">Our promise: keep the experience simple, modern, and human.</p>
              <div class="grid gap-4">
                <div class="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                  <p class="text-sm text-gray-200">Invitations and RSVPs</p>
                  <p class="text-lg font-semibold text-white">Together</p>
                </div>
                <div class="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                  <p class="text-sm text-gray-200">Venue and vendor discovery</p>
                  <p class="text-lg font-semibold text-white">Local</p>
                </div>
                <div class="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                  <p class="text-sm text-gray-200">Guest updates</p>
                  <p class="text-lg font-semibold text-white">Real-time</p>
                </div>
              </div>
            </div>
            <div class="rounded-3xl bg-white p-6 text-ink shadow-card ring-1 ring-gray-100">
              <p class="text-sm font-semibold text-ink">Our principles</p>
              <ul class="mt-3 space-y-3 text-sm text-muted">
                <li class="flex gap-3">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Hosts should be able to understand invitations, responses, and event activity at a glance.
                </li>
                <li class="flex gap-3">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Event professionals should have a clear way to present their business and receive enquiries.
                </li>
                <li class="flex gap-3">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Guests should receive the information and actions relevant to their invitation.
                </li>
              </ul>
              <div class="mt-6 rounded-2xl bg-gray-50 p-4">
                <p class="text-sm font-semibold text-ink">Offer event services?</p>
                <p class="text-sm text-muted">Create a vendor account and build a profile for your business.</p>
                <RouterLink class="mt-3 inline-flex items-center gap-2 rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" to="/vendor-signup">Become a vendor <i class="ri-arrow-right-line"></i></RouterLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div class="grid gap-6 md:grid-cols-2">
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-xs font-semibold uppercase text-muted">For hosts & planners</p>
            <h3 class="text-2xl font-semibold text-ink">Plan with less stress.</h3>
            <p class="mt-2 text-sm text-muted">Create invitations, manage guests, and keep event updates together.</p>
            <ul class="mt-4 space-y-2 text-sm text-muted">
              <li>• Personalized invitations and event details</li>
              <li>• Guest lists, responses, and feedback</li>
              <li>• Nearby venue and vendor discovery</li>
            </ul>
            <RouterLink class="mt-4 inline-flex rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" to="/register">Start planning</RouterLink>
          </div>
          <div class="rounded-3xl bg-gray-900 p-6 text-white">
            <p class="text-xs font-semibold uppercase text-gray-400">For vendors</p>
            <h3 class="text-2xl font-semibold">Grow with Invi8.</h3>
            <p class="mt-2 text-sm text-gray-300">Present your services and connect with hosts looking for event support.</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-300">
              <li>• Build your business profile</li>
              <li>• Add service or venue information</li>
              <li>• Receive and manage enquiries</li>
            </ul>
            <RouterLink class="mt-4 inline-flex rounded-full bg-white px-5 py-3 text-sm font-semibold text-ink transition hover:-translate-y-0.5 hover:shadow-card" to="/vendor-signup">Create vendor account</RouterLink>
          </div>
        </div>
      </section>

      <section class="bg-gray-50">
        <div class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <div class="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div class="space-y-3">
              <p class="text-xs font-semibold uppercase text-muted">Platform benefits</p>
              <h2 class="text-2xl font-semibold text-ink">One place for invites, RSVPs, and vendors.</h2>
              <ul class="mt-2 space-y-2 text-sm text-muted">
                <li>• Invitations, guest responses, feedback, and event activity in one workspace</li>
                <li>• Responsive invitation pages with photos, GIFs, posters, and videos</li>
                <li>• Nearby venue and vendor profiles with direct enquiry tools</li>
              </ul>
            </div>
            <div class="overflow-hidden rounded-3xl shadow-card ring-1 ring-gray-100">
              <img src="/assets/illustrations/welcome.svg" alt="Guests arriving at an event" class="h-full w-full bg-orange-50/60 object-contain p-8">
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white">
        <div class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <div class="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div class="overflow-hidden rounded-3xl shadow-card ring-1 ring-gray-100">
              <img src="/assets/illustrations/team-collaboration.svg" alt="Hosts and vendors collaborating" class="h-full w-full bg-[#fff5f7] object-contain p-8">
            </div>
            <div class="space-y-4">
              <p class="text-xs font-semibold uppercase text-muted">How Invi8 works</p>
              <ol class="space-y-2 text-sm text-muted list-decimal pl-4">
                <li>Create or pick an invite for the event type—add dates, venue, co-hosts.</li>
                <li>Search nearby venues and vendors, then review their profiles.</li>
                <li>Share invites, collect RSVPs, and sync updates across guests.</li>
                <li>Use feedback, event posts, shared media, and settings as the event evolves.</li>
              </ol>
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-100">
                  <p class="flex items-center gap-2 text-sm font-semibold text-ink">
                    <i class="ri-hand-coin-line text-brand"></i>
                    Vendor benefits
                  </p>
                  <p class="text-sm text-muted">Business profiles, service details, packages, venues, and enquiry management.</p>
                </div>
                <div class="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-100">
                  <p class="flex items-center gap-2 text-sm font-semibold text-ink">
                    <i class="ri-building-4-line text-brand"></i>
                    Venue benefits
                  </p>
                  <p class="text-sm text-muted">Venue profiles, spaces, packages, policies, galleries, and host enquiries.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-gray-50">
        <div class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
          <div class="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div class="overflow-hidden rounded-3xl bg-white p-4 shadow-card ring-1 ring-gray-100">
              <img :src="activeTab.img" :alt="activeTab.title" class="h-[360px] w-full rounded-2xl bg-orange-50/50 object-contain p-8 transition">
            </div>
            <div class="space-y-3">
              <button
                v-for="tab in featureTabs"
                :key="tab.id"
                class="flex w-full items-start gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-semibold text-ink shadow-card transition hover:-translate-y-0.5 hover:border-brand hover:text-brand"
                :class="activeTab.id === tab.id ? 'border-transparent bg-[#2f2f2f] text-white shadow-lg' : ''"
                @click="activeTabId = tab.id"
              >
                <span class="h-10 w-1 rounded-full" :class="activeTab.id === tab.id ? 'bg-white/80' : 'bg-brand/30'"></span>
                <div>
                  <p>{{ tab.title }}</p>
                  <p v-if="activeTab.id === tab.id" class="mt-1 text-xs font-normal" :class="activeTab.id === tab.id ? 'text-gray-200' : 'text-muted'">
                    {{ tab.copy }}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'AboutView',
  data () {
    return {
      activeTabId: 'sell',
      featureTabs: [
        {
          id: 'sell',
          title: 'Sell photos to event participants',
          copy: 'You can sell photos to event guests to generate revenue for your event.',
          img: '/assets/illustrations/welcome.svg'
        },
        {
          id: 'liveness',
          title: 'Liveliness Detection',
          copy: 'Keep your gallery trustworthy with automatic checks before publishing.',
          img: '/assets/illustrations/faq.svg'
        },
        {
          id: 'integration',
          title: 'Website Integration',
          copy: 'Embed invites, galleries, and vendor shortlists directly on your site.',
          img: '/assets/illustrations/team-collaboration.svg'
        },
        {
          id: 'branding',
          title: 'Branding & Promotions',
          copy: 'Lock colors, logos, and promo banners so every invite stays on-brand.',
          img: '/assets/illustrations/contact-us.svg'
        }
      ]
    }
  },
  computed: {
    activeTab () {
      return this.featureTabs.find(t => t.id === this.activeTabId) || this.featureTabs[0]
    }
  }
}
</script>
